import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { STANDARD_PATHS, STANDARD_PRICING } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const PlanCard = ({ path, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(path.key)}
    className={cn(
      "relative w-full text-left rounded-[12px] p-7 border transition-all duration-200 ease-smooth",
      selected
        ? "bg-bg-secondary border-accent-pink shadow-pink-glow-lg"
        : "bg-bg-primary border-border-default hover:border-accent-pink/60"
    )}
    data-testid={`plan-card-${path.key}`}
    aria-pressed={selected}
  >
    {path.badge && (
      <span className="absolute top-3 right-3 bg-accent-pink text-[#091424] font-bold text-[10px] tracking-wider px-2 py-1 rounded">
        {path.badge}
      </span>
    )}
    <div className="text-white font-extrabold text-[24px]">{path.duration}</div>
    <div className="text-accent-pink font-extrabold text-[32px] jea-mono mt-4">
      ${STANDARD_PRICING[1][path.key]}
    </div>
    <p className="text-text-secondary text-[13px] mt-3 leading-5">{path.subtext}</p>
  </button>
);

const DebtorPill = ({ count, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(count)}
    className={cn(
      "h-10 px-5 rounded-full border text-[14px] transition-all duration-150",
      active
        ? "bg-accent-pink text-[#091424] font-bold border-transparent"
        : "bg-bg-primary text-text-secondary border-border-default hover:border-accent-pink/60 font-medium"
    )}
    data-testid={`debtor-count-${count}`}
    aria-pressed={active}
  >
    {count} {count === 1 ? "Judgment" : "Judgments"}
  </button>
);

const PlanStandardStep = ({ data, patchSection }) => {
  const path = data.plan?.path || null;
  const debtorCount = data.plan?.debtor_count || null;

  const total = useMemo(() => {
    if (path && debtorCount) return STANDARD_PRICING[debtorCount]?.[path];
    return null;
  }, [path, debtorCount]);

  const selectedPathLabel = path
    ? `${STANDARD_PATHS.find((p) => p.key === path).duration}`
    : "—";

  return (
    <div className="space-y-6" data-testid="plan-standard-step">
      <div className="space-y-2">
        <h2 className="text-white font-bold text-[22px]">Choose Your Plan</h2>
        <p className="text-text-secondary text-[15px]">
          Select the subscription length that works for you. Per-query data broker costs apply to
          all plans.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4" data-testid="plan-cards">
        {STANDARD_PATHS.map((p) => (
          <PlanCard
            key={p.key}
            path={p}
            selected={path === p.key}
            onSelect={(k) => patchSection("plan", { path: k })}
          />
        ))}
      </div>

      <div className="space-y-3">
        <h3 className="text-white font-semibold text-[15px]">How many judgments will you manage?</h3>
        <div className="flex gap-2" data-testid="debtor-count-row">
          {[1, 2, 3].map((c) => (
            <DebtorPill
              key={c}
              count={c}
              active={debtorCount === c}
              onSelect={(v) => patchSection("plan", { debtor_count: v })}
            />
          ))}
        </div>
      </div>

      <motion.div
        initial={false}
        animate={{ opacity: total ? 1 : 0.6 }}
        className="bg-bg-tertiary rounded-[8px] px-5 py-4 border border-border-default"
        data-testid="pricing-summary"
      >
        <div className="flex items-start justify-between gap-6">
          <div className="space-y-1 text-[14px]">
            <div className="text-text-secondary">
              Plan: <span className="text-white font-semibold">{selectedPathLabel}</span>
            </div>
            <div className="text-text-secondary">
              Judgments: <span className="text-white font-semibold">{debtorCount ?? "—"}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="jea-caption">Total</div>
            <div className="jea-mono text-accent-pink font-bold text-[18px]" data-testid="pricing-total">
              {total ? `$${total}` : "—"}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PlanStandardStep;
